'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface FamilyMemberCardProps {
  member: {
    id: string;
    name: string;
    email: string;
    phone: string;
    avatar: string;
    alt: string;
    role: 'admin' | 'editor' | 'viewer';
    isOnline: boolean;
    lastActive: string;
  };
  onEditRole: (memberId: string, newRole: 'admin' | 'editor' | 'viewer') => void;
  onRemove: (memberId: string) => void;
  isCurrentUser?: boolean;
}

const FamilyMemberCard = ({ member, onEditRole, onRemove, isCurrentUser = false }: FamilyMemberCardProps) => {
  const [showMenu, setShowMenu] = useState(false);

  const getRoleBadgeColor = () => {
    switch (member.role) {
      case 'admin':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'editor':
        return 'bg-accent/10 text-accent border-accent/20';
      case 'viewer':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  return (
    <div className="glass p-3 lg:p-4 hover:shadow-warm-sm transition-smooth">
      <div className="flex items-start gap-3 lg:gap-4">
        <div className="relative flex-shrink-0">
          <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full overflow-hidden border-2 border-sage-200/30">
            <AppImage
              src={member.avatar}
              alt={member.alt}
              className="w-full h-full object-cover"
            />
          </div>
          {member.isOnline && (
            <span className="absolute bottom-0 right-0 w-2.5 h-2.5 lg:w-3 lg:h-3 rounded-full bg-success border-2 border-card"></span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-caption font-medium text-foreground text-sm lg:text-base break-words">
                  {member.name}
                  {isCurrentUser && (
                    <span className="ml-2 text-xs text-muted-foreground">(You)</span>
                  )}
                </h3>
              </div>
              <p className="text-xs lg:text-sm text-muted-foreground mb-1 truncate break-all">{member.email}</p>
              <p className="text-xs lg:text-sm text-muted-foreground truncate break-all">{member.phone}</p>
            </div>
            {!isCurrentUser && (
              <div className="relative ml-2">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-1 rounded hover:bg-muted transition-smooth"
                  aria-label="Member options"
                >
                  <Icon
                    name="EllipsisVerticalIcon"
                    size={20}
                    className="text-muted-foreground"
                  />
                </button>
                {showMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowMenu(false)}
                    />
                    <div className="absolute right-0 top-8 w-48 bg-popover border border-border rounded-lg shadow-warm-md z-50 py-1">
                      <div className="px-4 py-2 text-xs font-caption text-muted-foreground border-b border-border">
                        Change Role
                      </div>
                      <button
                        onClick={() => {
                          onEditRole(member.id, 'admin');
                          setShowMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm font-caption hover:bg-muted transition-smooth"
                      >
                        Admin
                      </button>
                      <button
                        onClick={() => {
                          onEditRole(member.id, 'editor');
                          setShowMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm font-caption hover:bg-muted transition-smooth"
                      >
                        Editor
                      </button>
                      <button
                        onClick={() => {
                          onEditRole(member.id, 'viewer');
                          setShowMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm font-caption hover:bg-muted transition-smooth"
                      >
                        Viewer
                      </button>
                      <div className="border-t border-border mt-1 pt-1">
                        <button
                          onClick={() => {
                            onRemove(member.id);
                            setShowMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm font-caption hover:bg-muted transition-smooth text-error"
                        >
                          Remove Member
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <span
              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-caption font-medium border ${getRoleBadgeColor()}`}
            >
              <Icon name="ShieldCheckIcon" size={12} />
              {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
            </span>
            <span className="text-xs font-caption text-muted-foreground">
              {member.isOnline ? 'Active now' : `Last active ${member.lastActive}`}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FamilyMemberCard;